// src/components/TeacherDetailPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Data and Components
import { TEACHER_PREFERENCE_STATUS } from '../constants/preferences';
import { updateTeacherQualifications, getScheduleEntries } from '../services/apiService';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import TeacherPreferenceGrid from './TeacherPreferenceGrid';
import SubjectQualificationModal from './SubjectQualificationModal';
import { parseSlotKey, formatTimeFromMinutes, timeToMinutes, getWeekNumber } from '../utils/helpers.ts';
import AssetManager from '../services/AssetManager.ts';
import HelpIcon from './Documentation/HelpIcon';
import AcademicYearService from '../services/AcademicYearService.ts';
import AssociatedClassesCard from './AssociatedClassesCard';
import EntityTagsSection from './EntityTagsSection';

// Icons (Keep as is)
const TrashIcon = ({ className = 'h-4 w-4' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);
const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);
const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

function TeacherDetailPage({ onUpdateTeacher, onDeleteTeacher }) {
    const { teacherId } = useParams();
    const navigate = useNavigate();
    const assetManager = useMemo(() => AssetManager.getInstance(), []);

    const [teacherData, setTeacherData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isNameEditModalOpen, setIsNameEditModalOpen] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedEmail, setEditedEmail] = useState('');
    const [isQualificationModalOpen, setIsQualificationModalOpen] = useState(false);

    // Schedule overlay state
    const [showScheduleOverlay, setShowScheduleOverlay] = useState(false);
    const [overlayPeriod, setOverlayPeriod] = useState('all'); // 'all' or period ID - shared with associated classes
    const [scheduleOverlayData, setScheduleOverlayData] = useState([]);
    const [availablePeriods, setAvailablePeriods] = useState([]);

    // Associated classes state
    const [associatedClasses, setAssociatedClasses] = useState([]);
    const [hoveredCourseIds, setHoveredCourseIds] = useState([]);

    const buildTeacherUpdatePayload = useCallback((overrides = {}) => {
        if (!teacherData) return null;
        return {
            id: teacherData.id,
            teacher_code: teacherData.teacherCode,
            name: teacherData.name,
            email: teacherData.email,
            tags: Array.isArray(teacherData.tags) ? teacherData.tags : [],
            preferences: Array.isArray(teacherData.preferences) ? teacherData.preferences : [],
            qualifiedSubjects: Array.isArray(teacherData.qualifiedSubjects) ? teacherData.qualifiedSubjects : [],
            ...overrides
        };
    }, [teacherData]);

    // Memoized list of qualified subjects for display
    const qualifiedSubjectObjectsForDisplay = useMemo(() => {
        if (!teacherData?.qualifiedSubjects) return [];
        // Filter out any subjects without names and sort safely
        return [...teacherData.qualifiedSubjects]
            .filter(s => s && s.name) // Filter out undefined/null subjects or those without names
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [teacherData?.qualifiedSubjects]);

    // Data Loading Effect
    useEffect(() => {
        const loadTeacherData = async () => {
            if (!teacherId) return;

            setIsLoading(true);
            setError(null);
            try {
                // Get teacher data including preferences using the new Teacher model
                const teacher = await assetManager.fetchTeacher(parseInt(teacherId));
                setTeacherData(teacher);
                setEditedName(teacher.name);
                setEditedEmail(teacher.email);
            } catch (err) {
                console.error("Error loading teacher:", err);
                setError(err instanceof Error ? err.message : "Failed to load teacher data");
                setTeacherData(null);
            } finally {
                setIsLoading(false);
            }
        };

        loadTeacherData();
    }, [teacherId, assetManager]);

       const loadTeacherTagSuggestions = useCallback(async () => {
        const teachers = await assetManager.fetchTeachers();
        const allTags = teachers
            .flatMap(teacher => Array.isArray(teacher.tags) ? teacher.tags : [])
            .map(tag => tag.trim())
            .filter(Boolean);
        const uniqueTags = Array.from(new Set(allTags.map(tag => tag.toLowerCase())))
            .map(lowerTag => allTags.find(tag => tag.toLowerCase() === lowerTag))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        return uniqueTags;
    }, [assetManager]);

    const persistTeacherTags = useCallback(async (tagsToSave) => {
        if (!teacherData) return;
        const payload = buildTeacherUpdatePayload({ tags: tagsToSave });
        if (!payload) return;
        const updatedTeacher = await onUpdateTeacher(payload);
        if (!updatedTeacher) {
            throw new Error('Teacher update did not return updated data');
        }
        setTeacherData(updatedTeacher);
    }, [onUpdateTeacher, teacherData, buildTeacherUpdatePayload]);

    // Fetch available periods from academic year structure
    useEffect(() => {
        const fetchPeriods = async () => {
            try {
                const academicYearService = AcademicYearService.getInstance();
                const yearStructure = await academicYearService.getAcademicYearStructure();

                if (yearStructure && yearStructure.periods) {
                    setAvailablePeriods(yearStructure.periods);
                }
            } catch (error) {
                console.error('Error fetching periods:', error);
                setAvailablePeriods([]);
            }
        };

        fetchPeriods();
    }, []);

    // Helper function to check if week series overlaps with a period
    const weekSeriesOverlapsPeriod = useCallback((weekSeries, periodStartWeek, periodEndWeek) => {
        if (!weekSeries) return false;

        // Parse week series to get individual week numbers
        const weekNumbers = new Set();
        const parts = weekSeries.split(',');

        for (const part of parts) {
            const trimmedPart = part.trim();
            if (trimmedPart.includes('-')) {
                const [startStr, endStr] = trimmedPart.split('-');
                const start = parseInt(startStr, 10);
                const end = parseInt(endStr, 10);

                if (!isNaN(start) && !isNaN(end)) {
                    if (start <= end) {
                        // Normal range
                        for (let i = start; i <= end; i++) {
                            weekNumbers.add(i);
                        }
                    } else {
                        // Cross-year range like "52-4"
                        for (let i = start; i <= 53; i++) {
                            weekNumbers.add(i);
                        }
                        for (let i = 1; i <= end; i++) {
                            weekNumbers.add(i);
                        }
                    }
                }
            } else {
                const weekNum = parseInt(trimmedPart, 10);
                if (!isNaN(weekNum)) {
                    weekNumbers.add(weekNum);
                }
            }
        }

        // Check if any week in the series falls within the period
        for (const week of weekNumbers) {
            if (periodStartWeek <= periodEndWeek) {
                // Normal period
                if (week >= periodStartWeek && week <= periodEndWeek) {
                    return true;
                }
            } else {
                // Cross-year period
                if (week >= periodStartWeek || week <= periodEndWeek) {
                    return true;
                }
            }
        }

        return false;
    }, []);

    // Fetch schedule overlay data when overlay is enabled
    useEffect(() => {
        const fetchScheduleOverlay = async () => {
            if (!showScheduleOverlay || !teacherData) {
                setScheduleOverlayData([]);
                return;
            }

            try {
                // Fetch all schedule entries
                const allEntries = await getScheduleEntries();

                // Find selected period if one is selected
                const selectedPeriod = overlayPeriod !== 'all'
                    ? availablePeriods.find(p => p.id?.toString() === overlayPeriod.toString())
                    : null;

                // Filter entries for this teacher
                const teacherEntries = allEntries.filter(entry => {
                    // Check if teacher matches
                    const teacherMatch =
                        entry.teacher_name === teacherData.name ||
                        (Array.isArray(entry.teachers) &&
                            entry.teachers.some(t => t.name === teacherData.name));

                    if (!teacherMatch) return false;

                    // Filter by period if a specific period is selected
                    if (selectedPeriod) {
                        const weekSeries = entry.week_series || entry.weekSeries;
                        if (!weekSeries) return false;

                        // Check if entry's week series overlaps with the selected period
                        return weekSeriesOverlapsPeriod(
                            weekSeries,
                            selectedPeriod.startWeek || selectedPeriod.start_week,
                            selectedPeriod.endWeek || selectedPeriod.end_week
                        );
                    }

                    return true;
                });

                setScheduleOverlayData(teacherEntries);
            } catch (error) {
                console.error('Error fetching schedule overlay:', error);
                setScheduleOverlayData([]);
            }
        };

        fetchScheduleOverlay();
    }, [showScheduleOverlay, teacherData, overlayPeriod, availablePeriods, weekSeriesOverlapsPeriod]);

    // Fetch and filter associated classes for the teacher
    useEffect(() => {
        const fetchAssociatedClasses = async () => {
            if (!teacherData) {
                setAssociatedClasses([]);
                return;
            }

            try {
                // Fetch all schedule entries
                const allEntries = await getScheduleEntries();

                // Find selected period if one is selected
                const selectedPeriod = overlayPeriod !== 'all'
                    ? availablePeriods.find(p => p.id?.toString() === overlayPeriod.toString())
                    : null;

                // Filter entries for this teacher
                const teacherEntries = allEntries.filter(entry => {
                    // Check if teacher matches
                    const teacherMatch =
                        entry.teacher_name === teacherData.name ||
                        (Array.isArray(entry.teachers) &&
                            entry.teachers.some(t => t.name === teacherData.name));

                    if (!teacherMatch) return false;

                    // Filter by period if a specific period is selected
                    if (selectedPeriod) {
                        const weekSeries = entry.week_series || entry.weekSeries;
                        if (!weekSeries) return false;

                        // Check if entry's week series overlaps with the selected period
                        return weekSeriesOverlapsPeriod(
                            weekSeries,
                            selectedPeriod.startWeek || selectedPeriod.start_week,
                            selectedPeriod.endWeek || selectedPeriod.end_week
                        );
                    }

                    return true;
                });

                // Transform the entries to match the expected format for display
                const transformedEntries = teacherEntries.map(entry => ({
                    id: entry.id,
                    subject: entry.subject_name,
                    groups: entry.groups || [],
                    room: entry.room_name,
                    day: entry.day,
                    time: entry.start_time && entry.end_time
                        ? `${entry.start_time} - ${entry.end_time}`
                        : 'Unscheduled',
                    weekSeries: entry.week_series || entry.weekSeries,
                    type: entry.entry_type
                }));

                setAssociatedClasses(transformedEntries);
            } catch (error) {
                console.error('Error fetching associated classes:', error);
                setAssociatedClasses([]);
            }
        };

        fetchAssociatedClasses();
    }, [teacherData, overlayPeriod, availablePeriods, weekSeriesOverlapsPeriod]);

    // Name/Email Edit Handlers
    const openNameEditModal = () => {
        if (teacherData) {
            setEditedName(teacherData.name);
            setEditedEmail(teacherData.email);
            setIsNameEditModalOpen(true);
        }
    };
    const closeNameEditModal = () => setIsNameEditModalOpen(false);
    const handleSaveNameAndEmail = async (e) => {
        e.preventDefault();
        if (!teacherData) return;

        if (!editedName.trim() || !editedEmail.trim()) {
            alert("Teacher name and email cannot be empty.");
            return;
        }
        if (!/\S+@\S+\.\S+/.test(editedEmail)) {
            alert('Please enter a valid email address.');
            return;
        }

        try {
            const payload = buildTeacherUpdatePayload({
                name: editedName.trim(),
                email: editedEmail.trim()
            });
            if (!payload) return;
            const updatedTeacher = await onUpdateTeacher(payload);
            if (!updatedTeacher) {
                throw new Error('Teacher update did not return updated data');
            }
            setTeacherData(updatedTeacher);
            closeNameEditModal();
        } catch (err) {
            console.error("Error updating teacher:", err);
            alert("Failed to update teacher details. Please try again.");
        }
    };

    // Preference Update Handlers
    const updateLocalPreferences = useCallback((newPreferences) => {
        setTeacherData(prev => prev ? {
            ...prev,
            preferences: newPreferences
        } : null);
    }, []);

    // --- Subject Qualification Modal Handlers ---
    const openQualificationModal = () => setIsQualificationModalOpen(true);
    const closeQualificationModal = () => setIsQualificationModalOpen(false);
    const handleSaveQualifications = useCallback(async (savedSubjectIds) => {
        if (!teacherId) return;

        try {
            const updatedSubjects = await updateTeacherQualifications(teacherId, savedSubjectIds);
            setTeacherData(prev => prev ? {
                ...prev,
                qualifiedSubjects: updatedSubjects
            } : null);
        } catch (err) {
            console.error("Error updating qualifications:", err);
            alert("Failed to update qualifications. Please try again.");
        }
    }, [teacherId]);

    // Save/Delete/Back Handlers
    const handleSaveChanges = async () => {
        if (!teacherData) return;
        try {
            const payload = buildTeacherUpdatePayload({
                preferences: teacherData.preferences
            });
            if (!payload) return;
            const updatedTeacher = await onUpdateTeacher(payload);
            if (!updatedTeacher) {
                throw new Error('Teacher update did not return updated data');
            }
            setTeacherData(updatedTeacher);
            navigate('/setup/teachers');
        } catch (err) {
            console.error("Error saving teacher:", err);
            alert("Failed to save changes. Please try again.");
        }
    };

    const handleDeleteClick = async () => {
        if (!teacherData) return;
        if (window.confirm(`Are you sure you want to delete teacher "${teacherData.name}"? This action cannot be undone.`)) {
            try {
                await onDeleteTeacher(teacherData.id);
                // Navigation back happens in App.jsx's handler
            } catch (err) {
                console.error("Error deleting teacher:", err);
                alert("Failed to delete teacher. Please try again.");
                navigate('/setup/teachers');
            }
        }
    };

    const handleGoBack = () => navigate('/setup/teachers');

    // --- Render Logic ---
    if (isLoading) return <div className="p-6 text-center">Loading teacher details...</div>;
    if (error) return (
        <div className="p-6 text-center text-red-600">
            <p>{error}</p>
            <Button variant="secondary" onClick={handleGoBack} className="mt-4">Back to Teacher List</Button>
        </div>
    );
    if (!teacherData) return (
        <div className="p-6 text-center text-gray-500">
            <p>Teacher data could not be loaded.</p>
            <Button variant="secondary" onClick={handleGoBack} className="mt-4">Back to Teacher List</Button>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Top Bar */}
            <div className="grid grid-cols-[auto_1fr_auto] items-center mb-4 gap-4">
                <Button variant="secondary" onClick={handleGoBack}>
                    &larr; Back to Teacher List
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        <h2 className="text-2xl font-bold text-gray-800 text-center whitespace-nowrap">
                            Teacher: {teacherData.name}
                            <span className="text-xl font-normal text-gray-500 ml-1">({teacherData.email})</span>
                        </h2>
                        <Button
                            variant="secondary"
                            onClick={openNameEditModal}
                            className="p-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
                            title="Edit teacher name and email"
                        >
                            <PencilIcon />
                        </Button>
                    </div>
                </div>
                <div className="min-w-[96px]" />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Column 1: Preferences */}
                <Card>
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-2 border-b pb-2">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-gray-700">Time Preferences</h3>
                                <HelpIcon documentationId="teacher-time-preferences" position="relative" />
                            </div>
                            <div className="flex items-center gap-2">
                                {showScheduleOverlay && (
                                    <select
                                        value={overlayPeriod}
                                        onChange={(e) => setOverlayPeriod(e.target.value)}
                                        className="text-xs border border-gray-300 rounded px-2 py-1"
                                    >
                                        <option value="all">All Periods</option>
                                        {availablePeriods.map(period => (
                                            <option key={period.id} value={period.id}>
                                                {period.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <div
                                    onClick={() => setShowScheduleOverlay(!showScheduleOverlay)}
                                    className={`cursor-pointer transition-colors ${showScheduleOverlay
                                        ? 'text-blue-600 hover:text-blue-700'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    title={showScheduleOverlay ? "Hide scheduled courses" : "Show scheduled courses"}
                                >
                                    <CalendarIcon />
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Click or drag grid cells to edit time preferences.</p>
                        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-600">
                            <span className="font-semibold">Legend:</span>
                            <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-green-200 mr-1 inline-block border border-gray-300"></span><span>Free</span></div>
                            <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-orange-200 mr-1 inline-block border border-gray-300"></span><span>Prefer Away</span></div>
                            <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-red-200 mr-1 inline-block border border-gray-300"></span><span>Cannot Teach</span></div>
                            {showScheduleOverlay && (
                                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-blue-500 bg-opacity-30 border-2 border-blue-500 mr-1 inline-block"></span><span>Scheduled</span></div>
                            )}
                        </div>
                        <TeacherPreferenceGrid
                            preferences={teacherData.preferences}
                            onPreferencesChange={updateLocalPreferences}
                            overlaySchedule={showScheduleOverlay ? scheduleOverlayData : []}
                            onScheduleHover={setHoveredCourseIds}
                        />
                    </div>
                </Card>

                {/* Column 2: Subject Qualifications */}
                <div className="space-y-6">
                    <Card>
                        <div className="p-4">
                            <div className="flex justify-between items-center mb-3 pb-2 border-b">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold text-gray-700">Subject Qualifications</h3>
                                    <HelpIcon documentationId="teacher-subject-qualifications" position="relative" />
                                </div>
                                <Button
                                    variant="secondary"
                                    className="text-xs py-1 px-2"
                                    onClick={openQualificationModal}
                                >
                                    Edit Qualifications
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {qualifiedSubjectObjectsForDisplay.length > 0 ? (
                                    qualifiedSubjectObjectsForDisplay.map(subject => (
                                        <div
                                            key={subject.id}
                                            className="px-2 py-1 rounded text-xs shadow-sm border border-gray-300"
                                            style={{ backgroundColor: subject.color || '#e0e0e0' }}
                                            title={subject.name}
                                        >
                                            <span className="font-medium mix-blend-difference text-white filter brightness-200 contrast-200">
                                                {subject.name}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500 italic w-full">No specific subject qualifications assigned.</p>
                                )}
                            </div>
                        </div>
                    </Card>

                    <AssociatedClassesCard
                        classes={associatedClasses}
                        filterPeriod={overlayPeriod}
                        onFilterChange={setOverlayPeriod}
                        availablePeriods={availablePeriods}
                        emptyMessage="No classes scheduled for this teacher"
                        entityType="teacher"
                        documentationId="teacher-associated-classes"
                        hoveredCourseIds={hoveredCourseIds}
                    />

                    <EntityTagsSection
                        tags={teacherData.tags}
                        onPersistTags={persistTeacherTags}
                        loadTagSuggestions={loadTeacherTagSuggestions}
                        documentationId="teacher-tags"
                        modalTitle="Edit Teacher Tags"
                        persistErrorMessage="Failed to update teacher tags. Please try again."
                    />
                </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-between items-center border-t pt-4">
                <Button variant="danger" onClick={handleDeleteClick} className="flex items-center space-x-1">
                    <TrashIcon />
                    <span>Delete Teacher</span>
                </Button>
                <div className="flex space-x-3">
                    <Button variant="secondary" onClick={handleGoBack}>Cancel</Button>
                    <Button variant="primary" onClick={handleSaveChanges}>Save Changes</Button>
                </div>
            </div>

            {/* Edit Name and Email Modal */}
            <Modal isOpen={isNameEditModalOpen} onClose={closeNameEditModal} title="Edit Teacher Details">
                <form onSubmit={handleSaveNameAndEmail}>
                    <div>
                        <label htmlFor="edit-teacher-name-modal" className="block text-sm font-medium text-gray-700 mb-1">Teacher Name</label>
                        <Input
                            id="edit-teacher-name-modal"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            placeholder="Enter teacher name"
                            required
                            autoFocus
                        />
                    </div>
                    <div>
                        <label htmlFor="edit-teacher-email-modal" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input
                            id="edit-teacher-email-modal"
                            type="email"
                            value={editedEmail}
                            onChange={(e) => setEditedEmail(e.target.value)}
                            placeholder="teacher@example.com"
                            required
                        />
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <Button type="button" variant="secondary" onClick={closeNameEditModal}>Cancel</Button>
                        <Button type="submit" variant="primary">Save Details</Button>
                    </div>
                </form>
            </Modal>

            {/* Subject Qualification Modal */}
            {teacherData && (
                <SubjectQualificationModal
                    isOpen={isQualificationModalOpen}
                    onClose={closeQualificationModal}
                    initialQualifiedSubjectIds={teacherData.qualifiedSubjects?.map(s => s.id) || []}
                    onSaveQualifications={handleSaveQualifications}
                    teacherName={teacherData.name}
                />
            )}
        </div>
    );
}

export default TeacherDetailPage;